// ./src/components/features/ExpenseSchedules/ExpenseSchedulesList/ExpenseSchedulesList.tsx
//
// List component for expense schedules.
// Renders schedule records as chronological cards with Framer Motion entrance
// animations — consistent with LogbookList, RevenuesList, and TireSetList.
//
// Context support:
// - When rendered inside an <ExpenseSchedulesProvider>, data props (schedules,
//   loading, loadingMore, hasMore, onLoadMore, deletingScheduleId) are read
//   from context automatically. Props can still override context values.
// - When rendered outside a provider, all data must be passed via props.
//
// Design decisions:
// - Cards only (no table view) — consistent across all breakpoints
// - Mobile: single-column vertical stack with SwipeableCard actions
// - Desktop (sm+): two-zone layout to use horizontal space efficiently:
//     Left zone  — name + status, amount, type badge, frequency + days
//     Right zone — next run, date range, location, notes
//     Top-right  — hover-reveal ActionIcon buttons (RecordCard pattern)
// - Status badge inline with name (always visible, key info at a glance)
// - Frequency + schedule days merged into a single line
// - Status-based left border color for quick visual scanning

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  ActionIcon,
  Tooltip,
  Skeleton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";

import {
  ExpenseSchedule,
  EXPENSE_SCHEDULE_STATUS,
  formatScheduleDaysForDisplay,
} from "@/utils/api";
import {
  useAppEssentialsContext,
  useUserProfileContext,
  useOptionalExpenseSchedulesContext,
  useAppEnvironment,
} from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  ExpenseScheduleStatusBadge,
  ExpenseScheduleTypeBadge,
} from "@/components/generic";
import { formatCurrency } from "@/utils";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

// ============================================
// Types
// ============================================

export interface ExpenseSchedulesListProps {
  /** Array of expense schedule records. Optional when inside provider. */
  schedules?: ExpenseSchedule[];

  /** Loading state for initial load. Optional when inside provider. */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEditSchedule?: (schedule: ExpenseSchedule) => void;

  /** Callback when delete action is clicked */
  onDeleteSchedule?: (schedule: ExpenseSchedule) => void;

  /** Callback when pause action is clicked */
  onPauseSchedule?: (schedule: ExpenseSchedule) => void;

  /** Callback when resume action is clicked */
  onResumeSchedule?: (schedule: ExpenseSchedule) => void;

  /** Callback when run now action is clicked */
  onRunNowSchedule?: (schedule: ExpenseSchedule) => void;

  /** Callback when a schedule card is clicked */
  onScheduleClick?: (schedule: ExpenseSchedule) => void;

  /** ID of schedule currently being deleted. Optional — reads from context. */
  deletingScheduleId?: string | null;

  /** ID of schedule currently being paused/resumed */
  togglingScheduleId?: string | null;

  /** ID of schedule currently running */
  runningScheduleId?: string | null;

  /** ID of recently edited/added schedule to highlight */
  highlightedScheduleId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddSchedule?: () => void;
}

// ============================================
// Constants
// ============================================

/** Map schedule status to left-border color */
const STATUS_BORDER_COLOR: Record<string, string> = {
  [EXPENSE_SCHEDULE_STATUS.ACTIVE]: "var(--mantine-color-green-5)",
  [EXPENSE_SCHEDULE_STATUS.PAUSED]: "var(--mantine-color-yellow-5)",
  [EXPENSE_SCHEDULE_STATUS.COMPLETED]: "var(--mantine-color-gray-4)",
};

// ============================================
// Hover-reveal CSS (matches RecordCard pattern)
// ============================================

const ACTIONS_CLASS = "schedule-card__actions";
const CARD_CLASS = "schedule-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }

  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-schedule-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ============================================
// Helper Functions
// ============================================

const formatDateRange = (
  startAt: string | null | undefined,
  endAt: string | null | undefined,
  t: (key: string) => string
): string | null => {
  if (!startAt && !endAt) return null;

  const startDate = startAt ? dayjs(startAt).format("MMM DD, YYYY") : null;
  const endDate = endAt ? dayjs(endAt).format("MMM DD, YYYY") : null;

  if (startDate && endDate) {
    return `${startDate} — ${endDate}`;
  } else if (startDate) {
    return t("expenseSchedules:list.startingFrom").replace("{{date}}", startDate);
  } else if (endDate) {
    return t("expenseSchedules:list.until").replace("{{date}}", endDate);
  }

  return null;
};

// ============================================
// Loading Skeletons
// ============================================

const ScheduleCardSkeleton: React.FC = () => (
  <Paper p="md" withBorder radius="md">
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start">
        <Group gap="sm" style={{ flex: 1 }}>
          <Skeleton height={22} width={180} />
          <Skeleton height={20} width={60} radius="xl" />
        </Group>
        <Skeleton height={28} width={90} />
      </Group>
      <Group gap="sm">
        <Skeleton height={20} width={70} radius="xl" />
        <Skeleton height={16} width={140} />
      </Group>
      <Group gap="lg" wrap="wrap">
        <Skeleton height={16} width={160} />
        <Skeleton height={16} width={200} />
        <Skeleton height={16} width={130} />
      </Group>
    </Stack>
  </Paper>
);

const LoadingSkeleton: React.FC = () => (
  <Stack gap="md">
    {Array.from({ length: 3 }).map((_, i) => (
      <ScheduleCardSkeleton key={i} />
    ))}
  </Stack>
);

// ============================================
// Detail Item — icon + text inline helper
// ============================================

const DetailItem: React.FC<{
  icon: string;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <Group gap={6} wrap="nowrap">
    <AppIcon iconName={icon} size={16} color="gray" style={{ flexShrink: 0 }} />
    <Text size="sm" c="dimmed" lineClamp={1}>
      {children}
    </Text>
  </Group>
);

// ============================================
// Schedule Card Component
// ============================================

interface ScheduleCardProps {
  schedule: ExpenseSchedule;
  expenseKindName: string;
  onClick?: (schedule: ExpenseSchedule) => void;
  onEdit?: (schedule: ExpenseSchedule) => void;
  onDelete?: (schedule: ExpenseSchedule) => void;
  onPause?: (schedule: ExpenseSchedule) => void;
  onResume?: (schedule: ExpenseSchedule) => void;
  onRunNow?: (schedule: ExpenseSchedule) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isToggling: boolean;
  isRunning: boolean;
  isMobile: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ScheduleCardComponent: React.FC<ScheduleCardProps> = ({
  schedule,
  expenseKindName,
  onClick,
  onEdit,
  onDelete,
  onPause,
  onResume,
  onRunNow,
  highlighted,
  isDeleting,
  isToggling,
  isRunning,
  isMobile,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["expenseSchedules", "common"]);
  const { userProfile } = useUserProfileContext();

  injectStyles();

  const isActive = schedule.status === EXPENSE_SCHEDULE_STATUS.ACTIVE;
  const isPaused = schedule.status === EXPENSE_SCHEDULE_STATUS.PAUSED;
  const isCompleted = schedule.status === EXPENSE_SCHEDULE_STATUS.COMPLETED;
  const isProcessing = isDeleting || isToggling || isRunning;

  // ── Computed display values ──

  const nextDateDisplay = useMemo(() => {
    if (!schedule.nextScheduledAt) return null;
    return dayjs(schedule.nextScheduledAt).format("MMM DD, YYYY");
  }, [schedule.nextScheduledAt]);

  const dateRangeDisplay = useMemo(() => {
    return formatDateRange(schedule.startAt, schedule.endAt, t);
  }, [schedule.startAt, schedule.endAt, t]);

  const frequencyDisplay = useMemo(() => {
    if (!schedule.scheduleType || !schedule.scheduleDays) return "";
    return formatScheduleDaysForDisplay(schedule.scheduleType, schedule.scheduleDays);
  }, [schedule.scheduleType, schedule.scheduleDays]);

  const amountDisplay = useMemo(() => {
    if (schedule.totalPrice == null) return null;
    return formatCurrency(
      schedule.totalPrice,
      schedule.paidInCurrency || userProfile?.homeCurrency || "USD"
    );
  }, [schedule.totalPrice, schedule.paidInCurrency, userProfile?.homeCurrency]);

  const borderLeftColor =
    STATUS_BORDER_COLOR[schedule.status || ""] || "var(--mantine-color-gray-3)";

  // ── Swipe actions (mobile) ──

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (isActive && onPause) {
      actions.push({
        key: "pause",
        icon: "PlayerPause",
        label: t("expenseSchedules:actions.pause"),
        color: "yellow",
        onClick: () => onPause(schedule),
        disabled: isProcessing,
        loading: isToggling,
      });
    }

    if (isPaused && onResume) {
      actions.push({
        key: "resume",
        icon: "PlayerPlay",
        label: t("expenseSchedules:actions.resume"),
        color: "green",
        onClick: () => onResume(schedule),
        disabled: isProcessing,
        loading: isToggling,
      });
    }

    if (!isCompleted && onRunNow) {
      actions.push({
        key: "runNow",
        icon: "Bolt",
        label: t("expenseSchedules:actions.runNow"),
        color: "orange",
        onClick: () => onRunNow(schedule),
        disabled: isProcessing,
        loading: isRunning,
      });
    }

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(schedule),
        disabled: isProcessing,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(schedule),
        loading: isDeleting,
        disabled: isProcessing,
      });
    }

    return actions;
  }, [isActive, isPaused, isCompleted, isProcessing, isToggling, isRunning, isDeleting, onPause, onResume, onRunNow, onEdit, onDelete, schedule, t]);

  // ── Event handlers ──

  const handleCardClick = useCallback(() => {
    if (!isProcessing && onClick) onClick(schedule);
  }, [isProcessing, onClick, schedule]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (!isProcessing) onClick?.(schedule);
      }
    },
    [onClick, schedule, isProcessing]
  );

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => onSwipeStateChange?.(isOpen),
    [onSwipeStateChange]
  );

  // ── Desktop hover-reveal actions (RecordCard pattern) ──

  const hasActions =
    onEdit || onDelete || (onPause && isActive) || (onResume && isPaused) || (onRunNow && !isCompleted);

  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {onPause && isActive && (
        <Tooltip label={t("expenseSchedules:actions.pause")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="yellow"
            size="md"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onPause(schedule); }}
            loading={isToggling}
            disabled={isProcessing}
            aria-label={t("expenseSchedules:actions.pause")}
          >
            <AppIcon iconName="PlayerPause" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onResume && isPaused && (
        <Tooltip label={t("expenseSchedules:actions.resume")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="green"
            size="md"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onResume(schedule); }}
            loading={isToggling}
            disabled={isProcessing}
            aria-label={t("expenseSchedules:actions.resume")}
          >
            <AppIcon iconName="PlayerPlay" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onRunNow && !isCompleted && (
        <Tooltip label={t("expenseSchedules:actions.runNow")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="orange"
            size="md"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRunNow(schedule); }}
            loading={isRunning}
            disabled={isProcessing}
            aria-label={t("expenseSchedules:actions.runNow")}
          >
            <AppIcon iconName="Bolt" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEdit(schedule); }}
            disabled={isProcessing}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDelete(schedule); }}
            loading={isDeleting}
            disabled={isProcessing}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ── Collect secondary detail items (dates, location, notes) ──

  const detailItems = useMemo(() => {
    const items: { icon: string; text: string }[] = [];

    if (nextDateDisplay && !isCompleted) {
      items.push({
        icon: "CalendarEvent",
        text: `${t("expenseSchedules:list.nextRun")}: ${nextDateDisplay}`,
      });
    }

    if (dateRangeDisplay) {
      items.push({ icon: "CalendarWeek", text: dateRangeDisplay });
    }

    if (schedule.whereDone) {
      items.push({ icon: "MapPin", text: schedule.whereDone });
    }

    if (schedule.shortNote) {
      items.push({ icon: "Note", text: schedule.shortNote });
    }

    return items;
  }, [nextDateDisplay, isCompleted, dateRangeDisplay, schedule.whereDone, schedule.shortNote, t]);

  // ── Card Paper shared props ──

  const cardPaperProps = {
    withBorder: true,
    radius: "md" as const,
    bg: highlighted ? ("green.0" as const) : undefined,
    opacity: isDeleting ? 0.5 : isCompleted ? 0.7 : 1,
    className: CARD_CLASS,
    style: {
      transition: "opacity 150ms ease, box-shadow 200ms ease",
      borderLeft: `3px solid ${borderLeftColor}`,
      overflow: "hidden" as const,
      cursor: onClick ? ("pointer" as const) : undefined,
      ...(highlighted
        ? {
          borderColor: "var(--mantine-color-green-5)",
          boxShadow:
            "0 0 0 1px var(--mantine-color-green-2), 0 4px 16px var(--mantine-color-green-1)",
        }
        : {}),
    },
    onClick: handleCardClick,
    onKeyDown: handleKeyDown,
    tabIndex: onClick ? 0 : undefined,
    role: onClick ? ("button" as const) : undefined,
  };

  // ============================================
  // MOBILE LAYOUT (<sm) — vertical single-column
  // ============================================

  const mobileLayout = (
    <Box hiddenFrom="sm">
      <Stack gap="sm" p="md">
        {/* Row 1: Kind name + Status (inline) ··· Amount */}
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
            <Text size="lg" fw={600} lineClamp={1}>
              {expenseKindName}
            </Text>
            <ExpenseScheduleStatusBadge status={schedule.status} size="sm" />
          </Group>
          {amountDisplay && (
            <Text size="xl" fw={700} c={isCompleted ? "dimmed" : "green.7"} style={{ flexShrink: 0 }}>
              {amountDisplay}
            </Text>
          )}
        </Group>

        {/* Car */}
        {schedule.car && (
          <Group gap={6}>
            <AppIcon iconName="Car" size={16} color="gray" />
            <Text size="sm" c="dimmed" lineClamp={1}>{schedule.car.label}</Text>
          </Group>
        )}

        {/* Type badge + frequency + days (combined) */}
        <Group gap="sm" wrap="wrap" align="center">
          <ExpenseScheduleTypeBadge scheduleType={schedule.scheduleType} size="sm" />
          {frequencyDisplay && (
            <DetailItem icon="Repeat">{frequencyDisplay}</DetailItem>
          )}
        </Group>

        {/* Detail items — stacked on mobile */}
        {detailItems.map((item, idx) => (
          <DetailItem key={idx} icon={item.icon}>{item.text}</DetailItem>
        ))}
      </Stack>
    </Box>
  );

  // ============================================
  // DESKTOP LAYOUT (sm+) — two-zone horizontal
  // ============================================
  //
  // ┌─────────────────────────────────────────────────────────────────────────┐
  // │ Financing/Leasing payment  ● PAUSED       [▶][⚡][✏️][🗑]   $869.00  │
  // │ 🚗 My Chrysler                                                         │
  // │ MONTHLY · 🔄 1st                           📅 Next: Feb 01, 2026      │
  // │                                             📅 Feb 01, 2026 — 2031    │
  // │                                             📍 Some Dealership!        │
  // │                                             📝 Loan payment            │
  // └─────────────────────────────────────────────────────────────────────────┘

  const desktopLayout = (
    <Box visibleFrom="sm">
      <Stack gap="sm" p="md">
        {/* Row 1: Kind name + Status ··· [hover actions] + Amount */}
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
            <Text size="lg" fw={600} lineClamp={1}>
              {expenseKindName}
            </Text>
            <ExpenseScheduleStatusBadge status={schedule.status} size="sm" />
          </Group>

          <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
            {hoverActionsEl}
            {amountDisplay && (
              <Text size="xl" fw={700} c={isCompleted ? "dimmed" : "green.7"}>
                {amountDisplay}
              </Text>
            )}
          </Group>
        </Group>

        {/* Row 2: Two-zone content area */}
        <Group
          gap="xl"
          wrap="nowrap"
          align="flex-start"
          style={{ minHeight: 0 }}
        >
          {/* LEFT ZONE: Car, type + frequency */}
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            {schedule.car && (
              <Group gap={6}>
                <AppIcon iconName="Car" size={16} color="gray" />
                <Text size="sm" c="dimmed" lineClamp={1}>{schedule.car.label}</Text>
              </Group>
            )}

            <Group gap="sm" wrap="wrap" align="center">
              <ExpenseScheduleTypeBadge scheduleType={schedule.scheduleType} size="sm" />
              {frequencyDisplay && (
                <DetailItem icon="Repeat">{frequencyDisplay}</DetailItem>
              )}
            </Group>
          </Stack>

          {/* RIGHT ZONE: Dates, location, notes */}
          {detailItems.length > 0 && (
            <Stack gap={4} style={{ flexShrink: 0, minWidth: 200 }}>
              {detailItems.map((item, idx) => (
                <DetailItem key={idx} icon={item.icon}>{item.text}</DetailItem>
              ))}
            </Stack>
          )}
        </Group>
      </Stack>
    </Box>
  );

  // ============================================
  // Render — mobile SwipeableCard / desktop plain card
  // ============================================

  if (isMobile && leftSwipeActions.length > 0) {
    return (
      <SwipeableCard
        leftSwipeActions={leftSwipeActions}
        forceClose={forceClose}
        onForceCloseComplete={onForceCloseComplete}
        onSwipeStateChange={handleSwipeStateChange}
        disabled={isProcessing}
        borderRadius="var(--mantine-radius-md)"
      >
        <Paper {...cardPaperProps}>
          {mobileLayout}
        </Paper>
      </SwipeableCard>
    );
  }

  return (
    <Paper {...cardPaperProps}>
      {mobileLayout}
      {desktopLayout}
    </Paper>
  );
};

const ScheduleCard = React.memo(ScheduleCardComponent);

// ============================================
// Main Component
// ============================================

const ExpenseSchedulesListComponent: React.FC<ExpenseSchedulesListProps> = ({
  schedules: schedulesProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditSchedule,
  onDeleteSchedule,
  onPauseSchedule,
  onResumeSchedule,
  onRunNowSchedule,
  deletingScheduleId: deletingScheduleIdProp,
  togglingScheduleId,
  runningScheduleId,
  highlightedScheduleId,
  onAddSchedule,
}) => {
  const { t } = useTranslation(["expenseSchedules", "common"]);
  const { isMobile } = useAppEnvironment();
  const { getExpenseKindNameById } = useAppEssentialsContext();

  // ── Context vs Props resolution ──
  const schedulesContext = useOptionalExpenseSchedulesContext();

  const schedules = schedulesProp ?? schedulesContext?.schedules ?? [];
  const loading = loadingProp ?? schedulesContext?.isLoading ?? false;
  const loadingMore = loadingMoreProp ?? schedulesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? schedulesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? schedulesContext?.loadMore;
  const deletingScheduleId = deletingScheduleIdProp ?? schedulesContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: schedules.length });

  // ── Get expense kind name with fallback ──

  const getKindName = useCallback(
    (schedule: ExpenseSchedule): string => {
      if (schedule.expenseKind?.name) return schedule.expenseKind.name;
      if (schedule.kindId != null) {
        const name = getExpenseKindNameById(schedule.kindId);
        if (name) return name;
      }
      return t("expenseSchedules:list.unknownKind");
    },
    [getExpenseKindNameById, t]
  );

  // ── Swipe state management ──

  const handleSwipeStateChange = useCallback(
    (scheduleId: string, isOpen: boolean) => {
      if (isOpen) openCard(scheduleId);
    },
    [openCard]
  );

  // ── Action callbacks ──

  const handleEdit = useCallback(
    (schedule: ExpenseSchedule) => {
      closeAllCards();
      onEditSchedule?.(schedule);
    },
    [onEditSchedule, closeAllCards]
  );

  const handleDelete = useCallback(
    (schedule: ExpenseSchedule) => {
      closeAllCards();
      onDeleteSchedule?.(schedule);
    },
    [onDeleteSchedule, closeAllCards]
  );

  // ── Loading state ──

  if (loading && schedules.length === 0) {
    return <LoadingSkeleton />;
  }

  // ── Empty state ──

  if (!loading && schedules.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("expenseSchedules:emptyState.title")}
          description={t("expenseSchedules:emptyState.description")}
          iconName="CalendarClock"
          actionLabel={t("expenseSchedules:emptyState.actionLabel")}
          onAction={onAddSchedule}
          size="md"
        />
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-md)",
        }}
      >
        <AnimatePresence mode="sync">
          {schedules.map((schedule) => (
            <motion.div
              key={schedule.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <ScheduleCard
                schedule={schedule}
                expenseKindName={getKindName(schedule)}
                //onClick={onScheduleClick ? handleClick : undefined}
                onEdit={onEditSchedule ? handleEdit : undefined}
                onDelete={onDeleteSchedule ? handleDelete : undefined}
                onPause={onPauseSchedule}
                onResume={onResumeSchedule}
                onRunNow={onRunNowSchedule}
                highlighted={schedule.id === highlightedScheduleId}
                isDeleting={deletingScheduleId === schedule.id}
                isToggling={togglingScheduleId === schedule.id}
                isRunning={runningScheduleId === schedule.id}
                isMobile={!!isMobile}
                forceClose={shouldForceClose(schedule.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(schedule.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(schedule.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const ExpenseSchedulesList = React.memo(ExpenseSchedulesListComponent);